import React, { useEffect, useState } from 'react';
import { useMutation } from '@apollo/client';
import { SELLER_UPDATE_ORDER_STATUS } from 'api/graphql/mutations/updateOrderStatus';
import { Spinner } from 'react-bootstrap';
import FeatherIcon from 'feather-icons-react';
import Button from 'components/base/Button';
import { Order as OrderType } from 'data/e-commerce/orders';
import { showStatusUpdateToast, showErrorToast } from './ToastManager';
import { getNextStatus } from 'utils/orderStatusHelpers';
import { useTranslation } from 'react-i18next';
import i18n from 'i18next';

interface BulkStatusUpdateButtonProps {
  selectedRows: OrderType[];
  onStatusUpdate: () => void;
  onError: (message: string) => void;
}

const BulkStatusUpdateButton: React.FC<BulkStatusUpdateButtonProps> = ({ 
  selectedRows, 
  onStatusUpdate,
  onError
}) => {
  const { t } = useTranslation(); // Add translation hook
  const [isVisible, setIsVisible] = useState(false);
  const [isFadingOut, setIsFadingOut] = useState(false);
  const [uniformStatus, setUniformStatus] = useState<string | null>(null);
  const [targetStatus, setTargetStatus] = useState<string | null>(null);
  const [isUpdating, setIsUpdating] = useState(false);

  // GraphQL mutation for updating order status
  const [updateOrderStatus, { loading: updateStatusLoading }] = useMutation(SELLER_UPDATE_ORDER_STATUS, {
    onCompleted: () => {
      setIsUpdating(false);
      
      // Show toast notification about how many orders were updated
      if (targetStatus) {
        showStatusUpdateToast(targetStatus, selectedRows.length);
      }
      
      // Hide the button immediately after successful update
      setIsVisible(false);
      setTargetStatus(null);
      setUniformStatus(null);
      
      // Notify parent component to refetch data
      onStatusUpdate();
    },
    onError: (error) => {
      setIsUpdating(false);
      const errorMessage = error.message;
      showErrorToast(errorMessage);
      onError(errorMessage);
    }
  });
  
  // Function to handle bulk order status update
  const handleBulkUpdateOrderStatus = () => {
    if (!targetStatus || selectedRows.length === 0) {
      return;
    }
    
    setIsUpdating(true);
    
    // Extract the order IDs from selected rows
    const orderIds = selectedRows.map(order => parseInt(order.orderId.toString(), 10));
    
    // Send all order IDs in a single update call
    updateOrderStatus({
      variables: {
        orderIds: orderIds,
        orderStatus: targetStatus
      }
    }).catch(error => {
      console.error('Error updating order statuses:', error);
    });
  };

  // Determine if selected orders have a uniform status and what the target status should be
  useEffect(() => {
    if (selectedRows.length === 0) {
      setUniformStatus(null);
      setTargetStatus(null);
      
      // If button is visible, start fade out animation
      if (isVisible) {
        startFadeOut();
      }
      return;
    }
    
    // Extract actual status labels from selected rows
    const statusLabels = selectedRows.map(row => 
      row.fulfilment_status?.label?.toLowerCase() || ''
    );
    
    // Check if all status labels are the same
    const firstStatusLabel = statusLabels[0];
    const hasUniformStatus = statusLabels.every(label => label === firstStatusLabel);
    
    if (hasUniformStatus) {
      // Get the status from the first selected row (all are the same)
      const actualStatus = statusLabels[0];
      
      // Determine next status
      const nextStatus = getNextStatus(actualStatus);
      
      setUniformStatus(actualStatus);
      setTargetStatus(nextStatus);
      
      // Show button if we have a valid target status
      if (nextStatus) {
        setIsVisible(true);
        
        // Apply fade-in animation
        setTimeout(() => {
          const button = document.getElementById('bulk-status-update-button');
          if (button) {
            button.classList.add('fade-in');
            button.classList.remove('fade-out');
          }
        }, 10);
      } else if (isVisible) {
        startFadeOut();
      }
    } else if (isVisible) {
      // If statuses are not uniform and button is visible, hide it
      startFadeOut();
    }
  }, [selectedRows]);

  // Helper to start the fade-out animation
  const startFadeOut = () => {
    const button = document.getElementById('bulk-status-update-button');
    if (button) {
      setIsFadingOut(true);
      button.classList.add('fade-out');
      button.classList.remove('fade-in');
      
      // After animation completes, update state
      setTimeout(() => {
        setIsVisible(false);
        setUniformStatus(null);
        setTargetStatus(null);
        setIsFadingOut(false);
      }, 500); // Match this with CSS transition time
    }
  };

  // If not visible, don't render anything
  if (!isVisible) {
    return null;
  }
  
  // Determine button variant based on the target status
  const getButtonVariant = () => {
    if (targetStatus === 'En_cour_de_preparation') return 'primary';
    if (targetStatus === 'prepared') return 'success';
    if (targetStatus === 'En_cour_d_enlevement') return 'info';
    return 'primary';
  };
  
  // Determine icon based on the target status
  const getButtonIcon = () => {
    if (targetStatus === 'En_cour_de_preparation') return 'package';
    if (targetStatus === 'prepared') return 'check-circle';
    if (targetStatus === 'En_cour_d_enlevement') return 'truck';
    return 'arrow-right';
  };
  
  // Determine button text based on the target status with language-aware translation
  const getButtonText = () => {
    const currentLanguage = i18n.language;
    
    // Helper function to get appropriate label based on language
    const getLabel = (englishLabel: string, translationKey: string): string => {
      // For English, show the original English label
      if (currentLanguage === 'en') {
        return englishLabel;
      }
      // For other languages (French, Arabic), use the translation system
      return t(translationKey);
    };
    
    if (targetStatus === 'En_cour_de_preparation') 
      return getLabel('Prepare', 'orderDetails.prepare');
    if (targetStatus === 'prepared') 
      return getLabel('Finish Preparing', 'orderDetails.finishPreparing');
    if (targetStatus === 'En_cour_d_enlevement') 
      return getLabel('In Transit', 'orderDetails.inTransit');
    return getLabel('Update Status', 'common.updateStatus');
  };

  return (
    <Button 
      id="bulk-status-update-button"
      variant={getButtonVariant()}
      className="me-2"
      disabled={isUpdating || isFadingOut}
      onClick={handleBulkUpdateOrderStatus}
      style={{ opacity: 0 }} // Start hidden, animation will show it
    >
      {isUpdating ? (
        <>
          <Spinner
            as="span"
            animation="border"
            size="sm"
            role="status"
            aria-hidden="true"
            className="me-2"
            style={{ width: '0.9rem', height: '0.9rem' }}
          />
          <span>{t('orderDetails.updating')}</span>
        </>
      ) : (
        <>
          <FeatherIcon icon={getButtonIcon()} size={14} className="me-2" />
          {getButtonText()}
        </>
      )}
    </Button>
  );
};

export default BulkStatusUpdateButton;
